import {
  IconAntennaBars4,
  IconBasketDiscount,
  IconUserHeart,
  IconUserPlus,
  IconUsersGroup,
} from '@tabler/icons-react'
import ReactEcharts from 'echarts-for-react'
import { useEffect } from 'react'

import {
  ContentCard,
  Leaderboard,
  MetricCard,
  ScalrCarousel,
} from '@/components/customComponents'
import { TempLoyaltyChart } from '@/components/ui/charts'

import { metricCardColors } from '@/constants/COLOR_PALETTE'
import { toast, useLoyaltyMetrics } from '@/hooks'
import {
  selectPageRefreshTrigger,
  useAppSelector,
  useReduxBaseFilter,
} from '@/store'
import { KpiGoalsModel } from '@/types/clarity'
import { dateToString } from '@/utils'
import { DateType } from '@/utils/date'

export default function LoyaltyPage({
  kpiGoals,
  kpiGoalsLoading,
}: {
  kpiGoals: KpiGoalsModel | undefined
  kpiGoalsLoading: boolean
}) {
  const { selectedBranch, date, dateFilterType } = useReduxBaseFilter()

  const refreshTrigger = useAppSelector(selectPageRefreshTrigger('loyalty'))

  const {
    branchData,
    companyData,
    branchTrendData,
    companyTrendData,
    metricCardData,
    chartData,
    personaData,
    personaLeaderboardColumns,
    frequencyChart7DaysData,
    frequencyChart30DaysData,
    churnRateChartData,
    orderCountBracketsChartData,
    loading,
    error,
    refetch,
  } = useLoyaltyMetrics(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    dateFilterType === 'test-range'
      ? 'month-to-date'
      : (dateFilterType as DateType),
    selectedBranch
  )

  // const {
  //   processedData,
  //   loading: regionCardsLoading,
  //   error: regionCardsError,
  //   refetch: regionCardsRefetch,
  // } = useLoyaltyRegionBoxMetrics(
  //   dateToString(date, 'from'),
  //   dateToString(date, 'to'),
  //   dateFilterType as DateType
  // )

  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing loyalty page data...')
      refetch()
      // regionCardsRefetch()
    }
  }, [refreshTrigger, refetch])

  useEffect(() => {
    if (error) {
      toast({
        title: 'Error fetching labor metrics',
        description: error?.message,
        variant: 'destructive',
      })
    }
  }, [branchData, selectedBranch, error])

  return (
    <div className="space-y-6">
      <div className="space-y-3 mb-10">
        <ScalrCarousel>
          <MetricCard
            title="Loyalty"
            mtdCurrContent={
              metricCardData?.currMetricsMonthly?.loyaltyPercentage
            }
            ytdCurrContent={
              metricCardData?.currMetricsYearly?.loyaltyPercentage
            }
            mtdPrevContent={
              metricCardData?.prevMetricsMonthly?.loyaltyPercentage
            }
            ytdPrevContent={
              metricCardData?.prevMetricsYearly?.loyaltyPercentage
            }
            content={companyData?.loyaltyPercentage?.toFixed(2)}
            type="percentage"
            kpiGoal={kpiGoals?.loyalty}
            loading={loading || kpiGoalsLoading}
            icon={<IconUsersGroup color="white" size={20} />}
            color={metricCardColors.blue500}
          />
          <MetricCard
            title="New User"
            mtdCurrContent={
              metricCardData?.currMetricsMonthly?.newUserPercentage
            }
            ytdCurrContent={
              metricCardData?.currMetricsYearly?.newUserPercentage
            }
            mtdPrevContent={
              metricCardData?.prevMetricsMonthly?.newUserPercentage
            }
            ytdPrevContent={
              metricCardData?.prevMetricsYearly?.newUserPercentage
            }
            content={companyData?.newUserPercentage?.toFixed(2)}
            type="percentage"
            kpiGoal={kpiGoals?.newUser}
            loading={loading || kpiGoalsLoading}
            icon={<IconUserPlus color="white" size={20} />}
            color={metricCardColors.red500}
          />
          <MetricCard
            title="7 Day Frequency"
            mtdCurrContent={
              metricCardData?.currMetricsMonthly?.avgFrequency7day
            }
            ytdCurrContent={metricCardData?.currMetricsYearly?.avgFrequency7day}
            mtdPrevContent={
              metricCardData?.prevMetricsMonthly?.avgFrequency7day
            }
            ytdPrevContent={metricCardData?.prevMetricsYearly?.avgFrequency7day}
            content={companyData?.avgFrequency7day?.toFixed(2)}
            type="number"
            kpiGoal={kpiGoals?.sevenDaysFrequency}
            loading={loading || kpiGoalsLoading}
            icon={<IconAntennaBars4 color="white" size={20} />}
            color={metricCardColors.green500}
          />
          <MetricCard
            title="Retention Rate"
            mtdCurrContent={
              metricCardData?.currMetricsMonthly?.retentionPercentage
            }
            ytdCurrContent={
              metricCardData?.currMetricsYearly?.retentionPercentage
            }
            mtdPrevContent={
              metricCardData?.prevMetricsMonthly?.retentionPercentage
            }
            ytdPrevContent={
              metricCardData?.prevMetricsYearly?.retentionPercentage
            }
            content={companyData?.retentionPercentage?.toFixed(2)}
            type="percentage"
            kpiGoal={kpiGoals?.retentionRate}
            loading={loading || kpiGoalsLoading}
            icon={<IconUserHeart color="white" size={20} />}
            color={metricCardColors.orange500}
          />
          <MetricCard
            title="Discount Penetration"
            mtdCurrContent={
              metricCardData?.currMetricsMonthly?.discountPenetrationPercentage
            }
            ytdCurrContent={
              metricCardData?.currMetricsYearly?.discountPenetrationPercentage
            }
            mtdPrevContent={
              metricCardData?.prevMetricsMonthly?.discountPenetrationPercentage
            }
            ytdPrevContent={
              metricCardData?.prevMetricsYearly?.discountPenetrationPercentage
            }
            content={companyData?.discountPenetrationPercentage?.toFixed(2)}
            type="percentage"
            kpiGoal={kpiGoals?.discountPenetration}
            loading={loading || kpiGoalsLoading}
            icon={<IconBasketDiscount color="white" size={20} />}
            color={metricCardColors.blue500}
          />
        </ScalrCarousel>
      </div>

      <Leaderboard
        title="Persona Leaderboard"
        data={personaData}
        columns={personaLeaderboardColumns}
        loading={loading}
        sortBy="orderCount"
      />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="col-span-1 md:col-span-6">
          <ContentCard
            title="Frequency"
            loading={!frequencyChart7DaysData || !frequencyChart30DaysData}
          >
            {frequencyChart7DaysData && frequencyChart30DaysData && (
              <div className="grid grid-cols-2">
                <ReactEcharts option={frequencyChart7DaysData} />
                <ReactEcharts option={frequencyChart30DaysData} />
              </div>
            )}
          </ContentCard>
        </div>
        <div className="col-span-1 md:col-span-3">
          <ContentCard
            title="Churn Rate"
            infoDescription="The rate at which customers stop purchasing over a certain period"
            loading={!churnRateChartData}
          >
            {churnRateChartData && <ReactEcharts option={churnRateChartData} />}
          </ContentCard>
        </div>
        <div className="col-span-1 md:col-span-3">
          <ContentCard
            title="Order Count Brackets"
            loading={!orderCountBracketsChartData}
          >
            {orderCountBracketsChartData && (
              <ReactEcharts option={orderCountBracketsChartData} />
            )}
          </ContentCard>
        </div>
      </div>

      <TempLoyaltyChart first={false} second={true} />
    </div>
  )
}
